import { useRef, useState, useEffect, useCallback } from 'react';
import Map, { Marker, Source, Layer } from 'react-map-gl/mapbox';
import along from '@turf/along';
import length from '@turf/length';
import bearing from '@turf/bearing';
import { lineString } from '@turf/helpers';
import useWorldStore from '../store/worldStore';
import { resolveAct } from '../simulation/lifeSimEngine';
import { generateScene, getSceneUrls, getSceneKey, getSceneConfig } from '../services/sceneService';
import LocationCanvas from './LocationCanvas';
import SceneLoadingOverlay from './SceneLoadingOverlay';

const MAPBOX_TOKEN =
  process.env.NEXT_PUBLIC_MAPBOX_TOKEN || process.env.NEXT_PUBLIC_VITE_MAPBOX_TOKEN;

function getAgentCoords(agent) {
  if (agent.personalPlaces && agent.locationTag) {
    const placeKey = agent.locationTag.split('_')[0];
    const place = agent.personalPlaces[placeKey];
    if (place) return { lat: place.lat, lng: place.lng };
  }
  if (agent.homeCoords) return agent.homeCoords;
  if (agent.personalPlaces?.home) return agent.personalPlaces.home;
  return { lat: 40.7081, lng: -73.9571 };
}

function getPositionAlongRoute(routeGeometry, progress) {
  if (!routeGeometry?.coordinates?.length) return null;
  try {
    const line = lineString(routeGeometry.coordinates);
    const totalLength = length(line, { units: 'kilometers' });
    const distanceAlong = totalLength * Math.min(1, Math.max(0, progress));
    const point = along(line, distanceAlong, { units: 'kilometers' });
    const nextDist = Math.min(distanceAlong + 0.01, totalLength);
    const nextPoint = along(line, nextDist, { units: 'kilometers' });
    const routeBearing = bearing(point, nextPoint);
    return {
      lat: point.geometry.coordinates[1],
      lng: point.geometry.coordinates[0],
      bearing: routeBearing,
    };
  } catch {
    return null;
  }
}

function applyCyberpunkStyle(map) {
  const style = map.getStyle();
  if (!style || !style.layers) return;

  for (const layer of style.layers) {
    const id = layer.id;
    const type = layer.type;

    try {
      if (id.includes('water') && type === 'fill') {
        map.setPaintProperty(id, 'fill-color', '#060d1a');
      }

      if (id === 'background' || (id.includes('land') && type === 'fill')) {
        if (type === 'background') map.setPaintProperty(id, 'background-color', '#0c1018');
        else if (type === 'fill') map.setPaintProperty(id, 'fill-color', '#10141e');
      }

      if (id.includes('landuse') || id.includes('park') || id.includes('national')) {
        if (type === 'fill') map.setPaintProperty(id, 'fill-color', '#0c0a1e');
      }

      if (id.includes('road') || id.includes('street') || id.includes('bridge') || id.includes('tunnel')) {
        if (type === 'line') {
          map.setPaintProperty(id, 'line-color', '#00e5cc');
          map.setPaintProperty(id, 'line-opacity', 0.45);
        }
      }

      if (type === 'symbol') {
        if (id.includes('place-city') || id.includes('place-town') || id.includes('country')) {
          map.setPaintProperty(id, 'text-color', '#00ffcc');
          map.setPaintProperty(id, 'text-halo-color', '#000000');
          map.setPaintProperty(id, 'text-halo-width', 1.5);
        } else {
          map.setLayoutProperty(id, 'visibility', 'none');
        }
      }

      if (id.includes('building') && type === 'fill') {
        map.setPaintProperty(id, 'fill-color', '#0a0818');
      }
    } catch {}
  }
}

function addCustomLayers(map) {
  const labelLayer = map.getStyle().layers.find((layer) => layer.type === 'symbol');
  map.addLayer({
    id: 'cyberpunk-buildings-3d',
    source: 'composite',
    'source-layer': 'building',
    type: 'fill-extrusion',
    minzoom: 13,
    paint: {
      'fill-extrusion-color': '#12101f',
      'fill-extrusion-height': [
        'interpolate', ['linear'], ['zoom'],
        13, 0,
        14, ['get', 'height'],
      ],
      'fill-extrusion-base': ['get', 'min_height'],
      'fill-extrusion-opacity': 0.85,
    },
  }, labelLayer?.id);

  map.addLayer({
    id: 'building-neon-glow',
    source: 'composite',
    'source-layer': 'building',
    type: 'fill-extrusion',
    minzoom: 14,
    paint: {
      'fill-extrusion-color': '#00e5cc',
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': ['get', 'min_height'],
      'fill-extrusion-opacity': 0.06,
    },
  }, labelLayer?.id);
}

function AgentMapMarker({ agent, zoom, onSelect, onHover }) {
  const agentTravel = useWorldStore((state) => state.agentTravel?.[agent.id]);
  const [pos, setPos] = useState(() => getAgentCoords(agent));
  const [hovered, setHovered] = useState(false);
  const animRef = useRef(null);

  const actInfo = resolveAct(agent.activity) || { emoji: '●', label: agent.activity || 'idle' };
  const actEmoji = actInfo.emoji || '●';
  const [agentBearing, setAgentBearing] = useState(0);

  const currentPlaceKey = (agent.locationTag || 'home_idle').split('_')[0];
  const currentPlace = agent.personalPlaces?.[currentPlaceKey];
  const placeName = currentPlace?.name || agent.homeCity || 'Unknown';
  const fallbackCoords = getAgentCoords(agent);

  useEffect(() => {
    let resetFrame = null;

    if (agentTravel?.status !== 'traveling') {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      resetFrame = requestAnimationFrame(() => {
        setPos(fallbackCoords);
      });
      return () => {
        if (resetFrame) cancelAnimationFrame(resetFrame);
      };
    }

    const from = agentTravel.fromCoords;
    const to = agentTravel.toCoords;
    if (!from || !to) {
      resetFrame = requestAnimationFrame(() => {
        setPos(fallbackCoords);
      });
      return () => {
        if (resetFrame) cancelAnimationFrame(resetFrame);
      };
    }

    function tick() {
      const progress = Math.min(1, (Date.now() - agentTravel.travelStartTime) / agentTravel.travelDuration);

      if (agentTravel.routeGeometry) {
        const routePos = getPositionAlongRoute(agentTravel.routeGeometry, progress);
        if (routePos) {
          setPos({ lat: routePos.lat, lng: routePos.lng });
          setAgentBearing(routePos.bearing || 0);
          if (progress < 1) animRef.current = requestAnimationFrame(tick);
          return;
        }
      }

      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      setPos({
        lat: from.lat + (to.lat - from.lat) * eased,
        lng: from.lng + (to.lng - from.lng) * eased,
      });
      if (progress < 1) animRef.current = requestAnimationFrame(tick);
    }

    tick();
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (resetFrame) cancelAnimationFrame(resetFrame);
    };
  }, [
    agent,
    agentTravel?.fromCoords,
    agentTravel?.routeGeometry,
    agentTravel?.status,
    agentTravel?.toCoords,
    agentTravel?.travelDuration,
    agentTravel?.travelStartTime,
    fallbackCoords,
  ]);

  useEffect(() => {
    let resetFrame = null;

    if (agentTravel?.status !== 'traveling') {
      resetFrame = requestAnimationFrame(() => {
        setPos(fallbackCoords);
        setAgentBearing(0);
      });
    }

    return () => {
      if (resetFrame) cancelAnimationFrame(resetFrame);
    };
  }, [agent.activity, agent.locationTag, agentTravel?.status, agent, fallbackCoords]);

  const traveling = agentTravel?.status === 'traveling';

  return (
    <Marker longitude={pos.lng} latitude={pos.lat} anchor="center">
      <div
        style={{ cursor: 'pointer', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        onClick={() => onSelect(agent.id)}
        onMouseEnter={() => { setHovered(true); onHover?.(agent, true); }}
        onMouseLeave={() => { setHovered(false); onHover?.(agent, false); }}
      >
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: 28, height: 28,
          marginTop: -14, marginLeft: -14,
          borderRadius: '50%',
          border: `2px solid ${agent.color}`,
          animation: traveling ? 'agentPulse 0.5s ease-out infinite' : 'agentPulse 2s ease-out infinite',
          opacity: 0.6,
        }} />
        <div style={{
          width: hovered ? 16 : 12, height: hovered ? 16 : 12,
          borderRadius: traveling ? '2px' : '50%',
          background: agent.color,
          boxShadow: `0 0 ${hovered ? 12 : 8}px ${agent.color}, 0 0 ${hovered ? 24 : 16}px ${agent.color}44`,
          zIndex: 1, transition: 'all 0.15s ease',
          transform: traveling ? `rotate(${agentBearing - 90}deg)` : 'none',
          clipPath: traveling ? 'polygon(0% 20%, 60% 20%, 60% 0%, 100% 50%, 60% 100%, 60% 80%, 0% 80%)' : 'none',
        }} />
        {(zoom > 8 || hovered) && (
          <div style={{
            marginTop: 4,
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 9,
            color: agent.color, whiteSpace: 'nowrap',
            background: 'rgba(8,10,16,0.85)',
            borderRadius: 10, padding: '1px 7px',
            textShadow: `0 0 6px ${agent.color}44`,
          }}>
            {actEmoji} {agent.name}
          </div>
        )}
        {hovered && (
          <div style={{
            marginTop: 2,
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 8,
            background: 'rgba(8,10,16,0.92)',
            border: `1px solid ${agent.color}33`,
            borderRadius: 8, padding: '5px 12px',
            whiteSpace: 'nowrap', textAlign: 'center',
            color: 'rgba(255,255,255,0.7)',
            boxShadow: `0 0 12px ${agent.color}22`,
          }}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>
              {traveling ? '🚶' : actEmoji}
            </div>
            <div style={{ color: agent.color, marginBottom: 2, fontSize: 9, fontWeight: 600 }}>
              {traveling ? `Heading to ${agentTravel?.placeName || 'destination'}` : actInfo.name || actInfo.label || 'Idle'}
            </div>
            <div style={{ opacity: 0.5, fontSize: 8 }}>📍 {placeName}</div>
            {agent.homeCity && <div style={{ opacity: 0.3, fontSize: 7, marginTop: 1 }}>{agent.homeCity}</div>}
          </div>
        )}
      </div>
    </Marker>
  );
}

function ZoomedScene({ agent, onClose }) {
  const locationScenes = useWorldStore((state) => state.locationScenes);
  const activityType = agent?.activity || 'working';
  const placeKey = (agent.locationTag || 'home_idle').split('_')[0];
  const sceneKey = getSceneKey(activityType, agent.homeCity || placeKey || 'default');
  const sceneConfig = getSceneConfig(activityType);
  const scene = locationScenes[sceneKey];
  const isUnavailable = scene?.status === 'unavailable';
  const isReady = scene?.status === 'ready' || isUnavailable;
  const isGenerating = scene?.status === 'generating';
  const walkableShape = sceneConfig?.walkableShape || 'square';
  const backgroundUrl = scene?.status === 'ready'
    ? (scene?.directUrl || (scene?.id ? getSceneUrls(scene.id).background : null))
    : null;
  const referenceUrl = scene?.referenceUrl || null;
  const referenceSha256 = scene?.referenceSha256 || null;
  const activity = resolveAct(activityType) || { emoji: '●', label: activityType };

  useEffect(() => {
    if (agent) generateScene(activityType, sceneKey);
  }, [agent, activityType, sceneKey]);

  const agents = useWorldStore((state) => state.agents);
  const colocated = agents.filter((entry) => {
    if (entry.id === agent.id) return true;
    if (agent.homeCity && entry.homeCity === agent.homeCity) return true;
    return false;
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10, animation: 'fadeIn 0.3s ease-out' }}>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
        <LocationCanvas
          agents={colocated}
          backgroundUrl={backgroundUrl}
          walkableShape={walkableShape}
        />
      </div>
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)' }} />
      {!isReady && (
        <SceneLoadingOverlay
          key={`${sceneKey}:${scene?.status || 'idle'}`}
          activity={activity}
          isGenerating={isGenerating}
        />
      )}
      {isUnavailable && (
        <div style={{
          position: 'absolute', top: 56, right: 16, zIndex: 4,
          background: 'rgba(8,10,16,0.88)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8, padding: '6px 10px',
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.65)',
        }}>
          Background generation offline
        </div>
      )}
      {referenceUrl && (
        <div style={{
          position: 'absolute', top: isUnavailable ? 98 : 56, right: 16, zIndex: 4,
          width: 196, background: 'rgba(8,10,16,0.88)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8, padding: 10,
        }}>
          <div style={{
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.45)', marginBottom: 8,
          }}>
            Uploaded guide
          </div>
          <img
            src={referenceUrl}
            alt="Uploaded room-shell reference guide"
            style={{
              display: 'block', width: '100%', height: 96, objectFit: 'cover',
              borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', marginBottom: 8,
            }}
          />
          {referenceSha256 && (
            <div style={{
              fontFamily: "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace",
              fontSize: 9, lineHeight: 1.5, color: 'rgba(255,255,255,0.55)',
              wordBreak: 'break-all',
            }}>
              SHA-256: {referenceSha256}
            </div>
          )}
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 16, left: 16, zIndex: 4, display: 'flex', gap: 4, flexDirection: 'column' }}>
        {colocated.map((entry) => {
          const info = resolveAct(entry.activity) || { emoji: '●', label: 'idle' };
          return (
            <div key={entry.id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(8,10,16,0.85)', border: `1px solid ${entry.color}66`,
              borderRadius: 20, padding: '4px 12px',
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: entry.color }} />
              <span style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 10, color: 'white' }}>{entry.name}</span>
              <span style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
                {info.emoji} {info.name || info.label}
              </span>
            </div>
          );
        })}
      </div>
      <button onClick={onClose} style={{
        position: 'absolute', top: 56, left: 16, zIndex: 5,
        background: 'rgba(8,10,16,0.85)', border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 8, padding: '6px 14px', color: 'rgba(255,255,255,0.7)',
        fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 11, cursor: 'pointer',
      }}>← Back to globe</button>
    </div>
  );
}

export default function GlobeView() {
  const mapRef = useRef();
  const mapZoomTarget = useWorldStore((state) => state.mapZoomTarget);
  const setMapZoomTarget = useWorldStore((state) => state.setMapZoomTarget);
  const agents = useWorldStore((state) => state.agents);
  const agentTravelState = useWorldStore((state) => state.agentTravel);
  const zoomedAgent = mapZoomTarget ? agents.find((agent) => agent.id === mapZoomTarget) : null;

  const [zoom, setZoom] = useState(2);
  const lastPitchRef = useRef(0);
  const hoveredAgentRef = useRef(null);
  const middleMouseRef = useRef({ active: false, startY: 0, startZoom: 0, agentCoords: null });

  const handleMapLoad = useCallback((event) => {
    const map = event.target;
    applyCyberpunkStyle(map);
    addCustomLayers(map);
  }, []);

  const handleMove = useCallback((event) => {
    setZoom(event.viewState.zoom);
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map || !map.isStyleLoaded?.()) return;

    const targetPitch = zoom < 4 ? 0 : Math.min(60, (zoom - 4) * 6);
    const currentPitch = map.getPitch();

    if (Math.abs(currentPitch - targetPitch) > 3 && Math.abs(targetPitch - lastPitchRef.current) > 3) {
      lastPitchRef.current = targetPitch;
      map.easeTo({ pitch: targetPitch, duration: 400 });
    }
  }, [zoom]);

  useEffect(() => {
    if (!mapZoomTarget) return;
    const map = mapRef.current?.getMap?.();
    const agent = agents.find((entry) => entry.id === mapZoomTarget);
    if (!map || !agent) return;

    const coords = getAgentCoords(agent);
    map.flyTo({
      center: [coords.lng, coords.lat],
      zoom: 15,
      pitch: 55,
      duration: 3000,
      essential: true,
    });
  }, [mapZoomTarget, agents]);

  const handleAgentSelect = useCallback((agentId) => {
    setMapZoomTarget(agentId);
  }, [setMapZoomTarget]);

  const handleAgentHover = useCallback((agent, isHovered) => {
    hoveredAgentRef.current = isHovered ? agent : null;
  }, []);

  const handleContainerMouseDown = useCallback((event) => {
    if (event.button !== 1) return;
    event.preventDefault();
    const agent = hoveredAgentRef.current;
    if (!agent) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const coords = getAgentCoords(agent);
    middleMouseRef.current = {
      active: true,
      startY: event.clientY,
      startZoom: map.getZoom(),
      agentCoords: coords,
    };
  }, []);

  const handleContainerMouseMove = useCallback((event) => {
    const state = middleMouseRef.current;
    if (!state.active) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const deltaY = state.startY - event.clientY;
    const zoomDelta = deltaY / 30;
    const targetZoom = Math.max(1, Math.min(18, state.startZoom + zoomDelta));
    const targetPitch = targetZoom > 10 ? Math.min(60, (targetZoom - 4) * 6) : 0;
    map.jumpTo({
      center: [state.agentCoords.lng, state.agentCoords.lat],
      zoom: targetZoom,
      pitch: targetPitch,
    });
  }, []);

  const handleContainerMouseUp = useCallback((event) => {
    if (event.button === 1) {
      middleMouseRef.current.active = false;
    }
  }, []);

  if (!MAPBOX_TOKEN) {
    return (
      <div style={{
        position: 'fixed', top: '40px', left: 0, right: 0, bottom: 0,
        background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 12,
      }}>
        <div style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 16, color: '#ff3366' }}>
          Mapbox token required
        </div>
        <div style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 11, color: '#666', maxWidth: 400, textAlign: 'center' }}>
          Add NEXT_PUBLIC_MAPBOX_TOKEN to your .env file. Get a free token at mapbox.com
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ position: 'fixed', top: '40px', left: 0, right: 0, bottom: 0, background: '#000' }}
      onMouseDown={handleContainerMouseDown}
      onMouseMove={handleContainerMouseMove}
      onMouseUp={handleContainerMouseUp}
      onContextMenu={(event) => { if (middleMouseRef.current.active) event.preventDefault(); }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: -40,
          latitude: 35,
          zoom: 1.8,
          pitch: 0,
          bearing: 0,
        }}
        projection="globe"
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        fog={{
          color: '#0a1628',
          'high-color': '#001a33',
          'horizon-blend': 0.04,
          'space-color': '#000408',
          'star-intensity': 1,
        }}
        onLoad={handleMapLoad}
        onMove={handleMove}
        maxPitch={65}
      >
        {agents.map((agent) => {
          const travel = agentTravelState[agent.id];
          if (travel?.status !== 'traveling' || !travel?.routeGeometry) return null;
          return (
            <Source key={`route-${agent.id}`} type="geojson" data={{
              type: 'Feature',
              geometry: travel.routeGeometry,
              properties: {},
            }}>
              <Layer type="line" paint={{
                'line-color': agent.color,
                'line-width': 3,
                'line-opacity': 0.5,
              }} />
              <Layer type="line" paint={{
                'line-color': agent.color,
                'line-width': 6,
                'line-opacity': 0.15,
                'line-blur': 4,
              }} />
            </Source>
          );
        })}

        {agents.map((agent) => (
          <AgentMapMarker
            key={agent.id}
            agent={agent}
            zoom={zoom}
            onSelect={handleAgentSelect}
            onHover={handleAgentHover}
          />
        ))}
      </Map>

      <style>{`
        .mapboxgl-ctrl-logo { display: none !important; }
        .mapboxgl-ctrl-attrib { opacity: 0.3; font-size: 9px !important; }
        .mapboxgl-ctrl-attrib:hover { opacity: 0.8; }
        @keyframes sceneLoadBar {
          0% { width: 0%; }
          50% { width: 80%; }
          100% { width: 100%; }
        }
        @keyframes agentPulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      `}</style>

      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.6) 100%)',
        zIndex: 1,
      }} />
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,200,0.015) 2px, rgba(0,255,200,0.015) 4px)',
      }} />

      <div style={{
        position: 'absolute', top: 56, right: 12, zIndex: 2,
        fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 8,
        color: 'rgba(255,255,255,0.3)',
        background: 'rgba(8,10,16,0.7)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 8, padding: '8px 12px',
        lineHeight: 1.8, pointerEvents: 'none',
      }}>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, marginBottom: 4, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Controls</div>
        <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>Scroll</span> - Zoom in/out</div>
        <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>Left drag</span> - Rotate globe</div>
        <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>Right drag</span> - Pan</div>
        <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>MMB drag ↕ on agent</span> - Quick zoom</div>
        <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>Click agent</span> - View scene</div>
      </div>

      {zoomedAgent && (
        <ZoomedScene agent={zoomedAgent} onClose={() => setMapZoomTarget(null)} />
      )}
    </div>
  );
}
